import React, { useContext, useEffect, useRef, useState } from 'react'
import type { GetRef, InputRef, TableProps } from 'antd'
import { Form, Input, Table } from 'antd'

type FormInstance<T> = GetRef<typeof Form<T>>

const EditableContext = React.createContext<FormInstance<any> | null>(
	null
)

interface EditableRowProps {
	index: number
}

const EditableRow: React.FC<EditableRowProps> = ({
	index,
	...props
}) => {
	const [form] = Form.useForm()
	return (
		<Form form={form} component={false}>
			<EditableContext.Provider value={form}>
				<tr {...props} />
			</EditableContext.Provider>
		</Form>
	)
}

interface EditableCellProps {
	title: React.ReactNode
	editable: boolean
	dataIndex: string
	record: any
	handleSave: (record: any) => void
}

const EditableCell: React.FC<
	React.PropsWithChildren<EditableCellProps>
> = ({
	title,
	editable,
	children,
	dataIndex,
	record,
	handleSave,
	...restProps
}) => {
	const [editing, setEditing] = useState(false)
	const inputRef = useRef<InputRef>(null)
	const form = useContext(EditableContext)!

	useEffect(() => {
		if (editing) {
			inputRef.current?.focus()
		}
	}, [editing])

	const toggleEdit = () => {
		setEditing(!editing)
		form.setFieldsValue({ [dataIndex]: record[dataIndex] })
	}

	const save = async () => {
		try {
			const values = await form.validateFields()
			toggleEdit()
			handleSave({ ...record, ...values })
		} catch (errInfo) {
			console.log('Save failed:', errInfo)
		}
	}

	let childNode = children

	if (editable) {
		childNode = editing ? (
			<Form.Item
				style={{ margin: 0 }}
				name={dataIndex}
				rules={[
					{
						required: true,
						message: `${title} is required.`
					}
				]}>
				<Input
					ref={inputRef}
					onPressEnter={save}
					onBlur={save}
				/>
			</Form.Item>
		) : (
			<div
				className="editable-cell-value-wrap"
				style={{ paddingInlineEnd: 24 }}
				onClick={toggleEdit}>
				{children}
			</div>
		)
	}

	return <td {...restProps}>{childNode}</td>
}

interface EditableTableProps<T> extends TableProps<T> {
	dataSource: T[]
	columns: (ColumnTypes[number] & {
		editable?: boolean
		dataIndex: string
	})[]
	handleAdd: () => void
	handleSave: (row: T) => void
}

type ColumnTypes = Exclude<TableProps<any>['columns'], undefined>

const EditableTable: React.FC<EditableTableProps<any>> = ({
	dataSource,
	columns,
	handleAdd,
	handleSave,
	...restProps
}) => {
	const components = {
		body: {
			row: EditableRow,
			cell: EditableCell
		}
	}

	const cols = columns.map((col) => {
		if (!col.editable) {
			return col
		}
		return {
			...col,
			onCell: (record: any) => ({
				record,
				editable: col.editable,
				dataIndex: col.dataIndex,
				title: col.title,
				handleSave
			})
		}
	})

	return (
		<div>
			<Table
				components={components}
				rowClassName={() => 'editable-row'}
				bordered
				dataSource={dataSource}
				columns={cols as ColumnTypes}
				{...restProps}
			/>
		</div>
	)
}

export default EditableTable
